/**
 * Tiny presentational widgets used inside InvestorDashboardPreview.
 * All values are decorative — no live data, no API calls.
 *
 * Components here are intentionally cheap and SVG-only so the modal
 * stays snappy on mobile.
 */
import { Fragment } from "react";
import { Lock, ArrowUpRight, Sparkles, Star, Crown } from "lucide-react";
import { formatUsd, formatUsdRange, formatUsdFull } from "./sampleData";

/* ---------------------------------------------------------------- */
/* Generic surfaces                                                   */
/* ---------------------------------------------------------------- */

export function SectionTitle({ eyebrow, title, hint, testid }) {
    return (
        <div className="mb-3" data-testid={testid}>
            {eyebrow && (
                <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium">
                    {eyebrow}
                </div>
            )}
            <h3 className="font-display font-semibold text-base sm:text-lg text-bl mt-1">
                {title}
            </h3>
            {hint && (
                <p className="text-xs text-bl-muted mt-0.5 leading-snug">{hint}</p>
            )}
        </div>
    );
}

export function StatTile({ label, value, sublabel, accent }) {
    return (
        <div className="bl-card p-4 sm:p-5">
            <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                {label}
            </div>
            <div
                className={
                    "mt-1.5 font-display text-2xl sm:text-[28px] font-semibold leading-none " +
                    (accent === "primary" ? "text-bl-primary" : "text-bl")
                }
            >
                {value}
            </div>
            {sublabel && (
                <div className="mt-1.5 text-[11px] text-bl-muted font-mono-bl truncate">
                    {sublabel}
                </div>
            )}
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Property cards / table                                            */
/* ---------------------------------------------------------------- */

function ScorePill({ score }) {
    const tone =
        score >= 70
            ? "bg-[#C85B3A]/15 text-[#C85B3A]"
            : score >= 40
              ? "bg-[#D4A62A]/15 text-[#A6831A]"
              : "bg-bl-teal-tint text-bl-primary";
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono-bl ${tone}`}
        >
            {score}
        </span>
    );
}

function HealthBar({ score }) {
    const tone =
        score >= 80
            ? "bg-bl-primary"
            : score >= 60
              ? "bg-[#D4A62A]"
              : "bg-[#C85B3A]";
    return (
        <div className="w-full h-1.5 rounded-full bg-bl-bg overflow-hidden">
            <div
                className={`h-full rounded-full ${tone}`}
                style={{ width: `${Math.max(8, Math.min(100, score))}%` }}
            />
        </div>
    );
}

export function PropertyCard({ property, isPremium }) {
    return (
        <div
            className="bl-card p-4 sm:p-5 flex flex-col gap-2.5"
            data-testid={`preview-property-${property.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-display font-medium text-bl truncate">
                        {property.address}
                    </div>
                    <div className="text-[11px] text-bl-muted truncate">
                        {property.city}
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {isPremium && property.report_type === "Premium" && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-bl-teal-tint text-bl-primary text-[9px] tracking-[0.16em] uppercase font-medium">
                            <Sparkles className="w-2.5 h-2.5" /> Premium
                        </span>
                    )}
                    <ScorePill score={property.risk_score} />
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between text-[11px] text-bl-muted mb-1">
                    <span>Health score</span>
                    <span className="font-mono-bl text-bl">{property.health_score}</span>
                </div>
                <HealthBar score={property.health_score} />
            </div>
            <div className="flex items-center justify-between text-[12px]">
                <div>
                    <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                        Repair exposure
                    </div>
                    <div className="font-mono-bl text-bl">
                        {formatUsdRange(
                            property.repair_exposure_low,
                            property.repair_exposure_high,
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                        High-priority
                    </div>
                    <div className="font-mono-bl text-bl">
                        {property.high_priority_issues}
                    </div>
                </div>
            </div>
            <p className="text-[12px] text-bl-muted leading-snug line-clamp-2">
                {property.top_issue}
            </p>
            <div className="text-[10px] text-bl-muted/80 mt-auto pt-1 flex items-center justify-between">
                <span>{property.analyzed_at}</span>
                <span className="text-bl-primary inline-flex items-center gap-0.5">
                    Open report <ArrowUpRight className="w-3 h-3" />
                </span>
            </div>
        </div>
    );
}

export function PropertyComparisonTable({ rows, withLeverage }) {
    return (
        <div className="bl-card overflow-x-auto" data-testid="preview-comparison-table">
            <table className="w-full text-xs sm:text-sm min-w-[640px]">
                <thead>
                    <tr className="text-left border-b border-bl">
                        <th className="px-3 py-2.5 text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                            Property
                        </th>
                        <th className="px-3 py-2.5 text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                            Risk
                        </th>
                        {withLeverage && (
                            <th className="px-3 py-2.5 text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                                Leverage
                            </th>
                        )}
                        <th className="px-3 py-2.5 text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                            Exposure
                        </th>
                        <th className="px-3 py-2.5 text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                            High-priority
                        </th>
                        <th className="px-3 py-2.5 text-[10px] tracking-[0.16em] uppercase text-bl-muted font-medium">
                            Type
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((p) => (
                        <tr
                            key={p.id}
                            className="border-b border-bl last:border-0 hover:bg-bl-bg/40"
                        >
                            <td className="px-3 py-2.5 text-bl font-medium whitespace-nowrap">
                                {p.address}
                                <div className="text-[10px] text-bl-muted">{p.city}</div>
                            </td>
                            <td className="px-3 py-2.5">
                                <ScorePill score={p.risk_score} />
                            </td>
                            {withLeverage && (
                                <td className="px-3 py-2.5">
                                    <span
                                        className={
                                            p.leverage === "strong"
                                                ? "text-bl-primary font-medium"
                                                : p.leverage === "weak"
                                                  ? "text-bl-muted"
                                                  : "text-bl"
                                        }
                                    >
                                        {p.leverage}
                                    </span>
                                </td>
                            )}
                            <td className="px-3 py-2.5 font-mono-bl text-bl whitespace-nowrap">
                                {formatUsdRange(p.repair_exposure_low, p.repair_exposure_high)}
                            </td>
                            <td className="px-3 py-2.5 font-mono-bl text-bl">
                                {p.high_priority_issues}
                            </td>
                            <td className="px-3 py-2.5 text-bl-muted">
                                {p.report_type === "Premium" ? (
                                    <span className="inline-flex items-center gap-0.5 text-bl-primary">
                                        <Sparkles className="w-3 h-3" /> Premium
                                    </span>
                                ) : (
                                    "Standard"
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Issue categories                                                  */
/* ---------------------------------------------------------------- */

export function IssueCategoryList({ categories }) {
    const maxCount = Math.max(...categories.map((c) => c.count));
    return (
        <div className="bl-card p-4 sm:p-5">
            <SectionTitle
                eyebrow="Issue categories"
                title="Where exposure concentrates"
                hint="Counts across analyzed properties · upper exposure estimate."
            />
            <ul className="space-y-2.5">
                {categories.map((c) => (
                    <li key={c.label} className="text-sm">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-bl">{c.label}</span>
                            <span className="font-mono-bl text-xs text-bl-muted">
                                {c.count} · {formatUsd(c.exposure)}
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-bl-bg overflow-hidden">
                            <div
                                className="h-full bg-bl-primary/70 rounded-full"
                                style={{
                                    width: `${Math.max(8, (c.count / maxCount) * 100)}%`,
                                }}
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Risk distribution chart (Pro+)                                    */
/* ---------------------------------------------------------------- */

export function RiskDistributionChart({ data }) {
    const total = data.reduce((s, d) => s + d.count, 0);
    const max = Math.max(...data.map((d) => d.count));
    const barWidth = 100 / data.length;
    return (
        <div className="bl-card p-4 sm:p-5">
            <SectionTitle
                eyebrow="Risk distribution"
                title="Portfolio risk profile"
                hint={`${total} analyzed properties · banded by inspection risk score.`}
            />
            <div className="relative h-32 sm:h-36">
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                    {data.map((d, i) => {
                        const h = (d.count / max) * 86;
                        return (
                            <g key={d.band}>
                                <rect
                                    x={i * barWidth + 2}
                                    y={94 - h}
                                    width={barWidth - 4}
                                    height={h}
                                    fill={d.color}
                                    rx="1.5"
                                />
                            </g>
                        );
                    })}
                </svg>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-[11px]">
                {data.map((d) => (
                    <div key={d.band} className="text-center">
                        <div className="font-mono-bl text-bl">{d.count}</div>
                        <div className="text-bl-muted">{d.band}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Negotiation opportunities (Pro+)                                  */
/* ---------------------------------------------------------------- */

export function NegotiationOppList({ opps }) {
    return (
        <div className="bl-card p-4 sm:p-5">
            <SectionTitle
                eyebrow="Negotiation opportunities"
                title="Concession sequencing ready to act on"
                hint="Premium-tier leverage analysis · estimated credit ranges, not guarantees."
            />
            <ul className="divide-y divide-bl/40">
                {opps.map((o) => (
                    <li key={o.address} className="py-2.5 first:pt-0 last:pb-0">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-display font-medium text-bl truncate">
                                    {o.address}
                                </div>
                                <div className="text-[12px] text-bl-muted leading-snug mt-0.5">
                                    {o.insight}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-bl-teal-tint text-bl-primary text-[10px] tracking-[0.14em] uppercase font-medium">
                                    {o.leverage}
                                </div>
                                <div className="mt-1 font-mono-bl text-xs text-bl whitespace-nowrap">
                                    {o.potential_credit}
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Activity feed                                                     */
/* ---------------------------------------------------------------- */

export function ActivityFeed({ items }) {
    return (
        <div className="bl-card p-4 sm:p-5">
            <SectionTitle eyebrow="Recent activity" title="Pipeline updates" />
            <ul className="space-y-2.5">
                {items.map((a, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-bl-primary shrink-0" />
                        <div className="min-w-0">
                            <div className="text-bl truncate">{a.label}</div>
                            <div className="text-[11px] text-bl-muted font-mono-bl">
                                {a.ts}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Plus-only widgets                                                 */
/* ---------------------------------------------------------------- */

export function RiskHeatmap({ heatmap }) {
    const { regions, categories, rows } = heatmap;
    return (
        <div className="bl-card p-4 sm:p-5 overflow-x-auto">
            <SectionTitle
                eyebrow="Risk heatmap"
                title="Issue concentration by region"
                hint="Darker cells = higher concentration of that issue across analyzed properties."
            />
            <div className="min-w-[420px]">
                <div
                    className="grid gap-1.5"
                    style={{
                        gridTemplateColumns: `120px repeat(${categories.length}, 1fr)`,
                    }}
                >
                    <div />
                    {categories.map((c) => (
                        <div
                            key={c}
                            className="text-[10px] tracking-[0.14em] uppercase text-bl-muted text-center"
                        >
                            {c}
                        </div>
                    ))}
                    {regions.map((r, ri) => (
                        <Fragment key={`region-${ri}`}>
                            <div className="text-[11px] text-bl truncate">{r}</div>
                            {rows[ri].map((v, ci) => (
                                <div
                                    key={`cell-${ri}-${ci}`}
                                    className="h-7 sm:h-8 rounded-md flex items-center justify-center text-[10px] font-mono-bl text-bl-surface"
                                    style={{
                                        backgroundColor: `rgba(15, 92, 99, ${0.18 + (v / 100) * 0.72})`,
                                    }}
                                >
                                    {v}
                                </div>
                            ))}
                        </Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function PropertyRankings({ rankings }) {
    return (
        <div className="bl-card p-4 sm:p-5">
            <SectionTitle
                eyebrow="Health rankings"
                title="Top-ranked properties"
                hint="Auto-ranked from health scores across portfolio."
            />
            <ol className="space-y-2">
                {rankings.map((r) => (
                    <li
                        key={r.rank}
                        className="flex items-center gap-3 text-sm"
                    >
                        <span className="w-7 h-7 rounded-full bg-bl-teal-tint text-bl-primary font-mono-bl text-xs flex items-center justify-center shrink-0">
                            {r.rank}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-bl truncate">{r.address}</div>
                            <div className="text-[11px] text-bl-muted">
                                {r.risk_band} risk · exposure ceiling{" "}
                                <span className="font-mono-bl">
                                    {formatUsd(r.exposure_high)}
                                </span>
                            </div>
                        </div>
                        <div className="font-mono-bl text-bl text-sm shrink-0">
                            {r.health_score}
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
}

export function CapitalPlanList({ plan }) {
    return (
        <div className="bl-card p-4 sm:p-5">
            <SectionTitle
                eyebrow="Capital planning"
                title="Long-term spend outlook"
                hint="Projected from stored inspection findings. Planning ranges, not bids."
            />
            <ul className="space-y-3">
                {plan.map((w) => (
                    <li key={w.window} className="text-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-bl font-medium">{w.window}</span>
                            <span className="font-mono-bl text-bl-primary text-xs">
                                {formatUsdFull(w.spend_low)} – {formatUsdFull(w.spend_high)}
                            </span>
                        </div>
                        <div className="text-[12px] text-bl-muted leading-snug mt-0.5">
                            {w.focus}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ---------------------------------------------------------------- */
/* Locked higher-tier teaser card                                    */
/* ---------------------------------------------------------------- */

export function LockedFeaturePreviewCard({ teaser, onUpgrade }) {
    const Ico = teaser.unlock_tier === "Investor Plus" ? Crown : Star;
    return (
        <div
            className="bl-card p-4 sm:p-5 relative border-dashed bg-bl-bg/40"
            data-testid={`preview-locked-${teaser.unlock_plan_id}`}
        >
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bl text-bl-surface text-[10px] tracking-[0.14em] uppercase font-medium">
                <Lock className="w-2.5 h-2.5" /> Locked
            </div>
            <div className="flex items-center gap-2 text-bl-primary">
                <Ico className="w-4 h-4" />
                <span className="text-[10px] tracking-[0.18em] uppercase font-medium">
                    {teaser.unlock_tier} feature
                </span>
            </div>
            <div className="mt-2 font-display font-semibold text-base text-bl">
                {teaser.title}
            </div>
            <p className="mt-1.5 text-[13px] text-bl-muted leading-snug">
                {teaser.description}
            </p>
            <button
                type="button"
                onClick={() => onUpgrade(teaser.unlock_plan_id)}
                className="mt-3 bl-btn-secondary !py-2 !px-3.5 !text-xs"
                data-testid={`preview-locked-upgrade-${teaser.unlock_plan_id}`}
            >
                Upgrade to {teaser.unlock_tier}
                <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
